import { ReactNode } from 'react';
import {
	AssetsPermissions,
	nullAssetsPermissions, ownerAssetsPermissions, projectManagerAssetsPermissions,
	projectEditorAssetsPermissions, projectReviewerAssetsPermissions,
	projectLimitedEditorAssetsPermissions
} from './permissions/assetsPermissions';
import {
	ProjectsPermissions,
	nullProjectsPermissions, ownerProjectsPermissions, projectManagerProjectsPermissions,
	projectEditorProjectsPermissions, projectReviewerProjectsPermissions,
	projectLimitedEditorProjectsPermissions
} from './permissions/projectsPermissions';
import {
	TasksPermissions,
	nullTasksPermissions, ownerTasksPermissions, projectManagerTasksPermissions,
	projectEditorTasksPermissions, projectReviewerTasksPermissions,
	projectLimitedEditorTasksPermissions
} from './permissions/tasksPermissions';
import {
	CommentsPermissions,
	nullCommentsPermissions, ownerCommentsPermissions, projectManagerCommentsPermissions,
	projectEditorCommentsPermissions, projectReviewerCommentsPermissions,
	projectLimitedEditorCommentsPermissions
} from './permissions/commentsPermissions';
import {
	UsersPermissions,
	nullUsersPermissions, ownerUsersPermissions, projectManagerUsersPermissions,
	projectEditorUsersPermissions, projectReviewerUsersPermissions,
	projectLimitedEditorUsersPermissions
} from './permissions/usersPermissions';

export type Entity = 'projects' | 'assets' | 'tasks' | 'comments' | 'users';
export type ProjectRolePermissionType = 'projectsPermissions' | 'assetsPermissions' | 'tasksPermissions' | 'commentsPermissions' | 'usersPermissions';

type EntityToPermissionsMap = {
	'projects': ProjectsPermissions;
	'assets': AssetsPermissions;
	'tasks': TasksPermissions;
	'comments': CommentsPermissions;
	'users': UsersPermissions;
}

export type Permit<T extends Entity> = keyof EntityToPermissionsMap[T];

export class ProjectRolePermissions {
	projectsPermissions: ProjectsPermissions;
	assetsPermissions: AssetsPermissions;
	tasksPermissions: TasksPermissions;
	commentsPermissions: CommentsPermissions;
	usersPermissions: UsersPermissions;

	constructor(
		projectsPermissions: ProjectsPermissions,
		assetsPermissions: AssetsPermissions,
		tasksPermissions: TasksPermissions,
		commentsPermissions: CommentsPermissions,
		usersPermissions: UsersPermissions
	) {
		this.projectsPermissions = projectsPermissions;
		this.assetsPermissions = assetsPermissions;
		this.tasksPermissions = tasksPermissions;
		this.commentsPermissions = commentsPermissions;
		this.usersPermissions = usersPermissions;
	}

	/**
	 * @param {bool} condition Accepts any boolean. Usually a selector of the permission
	 * @param {*} componentToShow
	 * @example RolePermissions.renderIfUserIsAllowed( getUserCanUploadAssets, <button /> )
	 */
	static renderIfUserIsAllowed( condition: boolean, componentToShow: ReactNode ) {
		return condition
			? componentToShow
			: null;
	}

	/**
	 * @param {'edit'|'add'|'destroy'|'archive'|'view'|'seeVersions'} permit
	 * @param {'projects'|'assets'|'markups'|'tasks'|'comments'|'users'} entity
	 * @example currentUserCan( 'edit', 'projects' ) // => true
	 */

	currentUserCan<E extends Entity>( permit: Permit<E>, entity: E ): boolean {
		const permission = `${entity}Permissions` as ProjectRolePermissionType;
		type PermKey = `${E}Permissions`;
		// Type assertion to help TypeScript understand the property exists
		const permissionObj = this[ permission as PermKey ];
		if ( !permissionObj || permissionObj[ permit as keyof typeof permissionObj ] === undefined ) {
			throw new Error( 'The requested permission does not exist' );
		}
		return permissionObj[ permit as keyof typeof permissionObj ] as boolean;
	}
}

export const ownerRolePermissions = new ProjectRolePermissions(
	ownerProjectsPermissions,
	ownerAssetsPermissions,
	ownerTasksPermissions,
	ownerCommentsPermissions,
	ownerUsersPermissions
);

export const projectManagerRolePermissions = new ProjectRolePermissions(
	projectManagerProjectsPermissions,
	projectManagerAssetsPermissions,
	projectManagerTasksPermissions,
	projectManagerCommentsPermissions,
	projectManagerUsersPermissions
);

export const projectEditorRolePermissions = new ProjectRolePermissions(
	projectEditorProjectsPermissions,
	projectEditorAssetsPermissions,
	projectEditorTasksPermissions,
	projectEditorCommentsPermissions,
	projectEditorUsersPermissions
);

export const projectReviewerRolePermissions = new ProjectRolePermissions(
	projectReviewerProjectsPermissions,
	projectReviewerAssetsPermissions,
	projectReviewerTasksPermissions,
	projectReviewerCommentsPermissions,
	projectReviewerUsersPermissions
);

export const projectLimitedEditorRolePermissions = new ProjectRolePermissions(
	projectLimitedEditorProjectsPermissions,
	projectLimitedEditorAssetsPermissions,
	projectLimitedEditorTasksPermissions,
	projectLimitedEditorCommentsPermissions,
	projectLimitedEditorUsersPermissions
);

export const nullRolePermissions = new ProjectRolePermissions(
	nullProjectsPermissions,
	nullAssetsPermissions,
	nullTasksPermissions,
	nullCommentsPermissions,
	nullUsersPermissions
);
